package one.healthium.uscreen.domain.models

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonClassDiscriminator
import one.healthium.uscreen.serialization.StyledTextModelSerializer

@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("type")
sealed interface UScreenBlock {
    val condition: BlockCondition? get() = null
}

@Serializable
@SerialName("image")
data class ImageBlock(
    @SerialName("src") val src: GenderText,
    @SerialName("height") val height: Float? = null,
    @SerialName("heightPx") val heightPx: Int? = null,
    @SerialName("contentScale") val contentScale: ImageFit = ImageFit.Fit,
    @SerialName("fillWidth") val fillWidth: Boolean = true,
    @SerialName("alignment") val alignment: ImageAlignment = ImageAlignment.Center,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = ImageBlock(
            src = GenderText("onboarding_welcome.png"),
            height = 40f,
        )
    }
}

@Serializable
@SerialName("spacer")
data class SpacerBlock(
    @SerialName("height") val height: Int? = null,
    @SerialName("width") val width: Int? = null,
    @SerialName("flex") val flex: Float? = null,
    @SerialName("safeArea") val safeArea: SafeAreaEdge? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
enum class SafeAreaEdge {
    @SerialName("top") Top,
    @SerialName("bottom") Bottom,
}

@Serializable
@SerialName("row")
data class RowBlock(
    @SerialName("blocks") val blocks: List<UScreenBlock>,
    @SerialName("verticalAlignment") val verticalAlignment: String? = null,
    @SerialName("horizontalArrangement") val horizontalArrangement: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("label")
data class LabelBlock(
    @SerialName("text") @Serializable(with = StyledTextModelSerializer::class) val text: StyledTextModel,
    @SerialName("typography") val typography: UScreenTypography = UScreenTypography.Body,
    @SerialName("align") val align: TextAlignment = TextAlignment.Center,
    @SerialName("color") val color: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = LabelBlock(
            text = StyledTextModel(
                listOf(
                    StyledTextModel.StyledSegment.Plain("What is your "),
                    StyledTextModel.StyledSegment.Styled(
                        text = "main goal",
                        fontWeight = UScreenFontWeight.Bold,
                    ),
                    StyledTextModel.StyledSegment.Plain("?"),
                ),
            ),
            typography = UScreenTypography.Title,
        )
    }
}

@Serializable
@SerialName("choice_list")
data class ChoiceListBlock(
    @SerialName("key") val key: String,
    @SerialName("single") val single: Boolean = true,
    @SerialName("animation") val animation: ChoiceAnimation = ChoiceAnimation.None,
    @SerialName("options") val options: List<UScreenChoiceOption>,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = ChoiceListBlock(
            key = "goal",
            single = true,
            animation = ChoiceAnimation.Stagger,
            options = listOf(
                UScreenChoiceOption.preview,
                UScreenChoiceOption(key = "health", text = GenderText("Get healthier"), emoji = GenderText("\uD83D\uDC81\u200D♀\uFE0F")),
                UScreenChoiceOption(key = "shape", text = GenderText("Stay in shape"), emoji = GenderText("\uD83C\uDF52")),
            ),
        )
    }
}

@Serializable
@SerialName("text_input")
data class TextInputBlock(
    @SerialName("key") val key: String,
    @SerialName("placeholder") val placeholder: String? = null,
    @SerialName("keyboard") val keyboard: KeyboardType = KeyboardType.Text,
    @SerialName("capitalization") val capitalization: TextCapitalization = TextCapitalization.None,
    @SerialName("autoFocus") val autoFocus: Boolean = false,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = TextInputBlock(
            key = "name",
            placeholder = "Enter your name",
            autoFocus = true,
        )
    }
}


@Serializable
@SerialName("number_picker")
data class NumberPickerBlock(
    @SerialName("key") val key: String,
    @SerialName("min") val min: Int = 0,
    @SerialName("max") val max: Int = 100,
    @SerialName("defaultValue") val defaultValue: Int = 50,
    @SerialName("label") val label: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = NumberPickerBlock(
            key = "age",
            min = 14,
            max = 100,
            defaultValue = 30,
            label = "years old",
        )
    }
}

@Serializable
@SerialName("vertical_ruler")
data class VerticalRulerBlock(
    @SerialName("key") val key: String,
    @SerialName("options") val options: List<RulerOption>,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("horizontal_ruler")
data class HorizontalRulerBlock(
    @SerialName("key") val key: String,
    @SerialName("options") val options: List<RulerOption>,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
enum class MeasurementUnit(val key: String, val isMetric: Boolean) {
    @SerialName("ft") Ft(key = "ft", isMetric = false),
    @SerialName("cm") Cm(key = "cm", isMetric = true),
    @SerialName("kg") Kg(key = "kg", isMetric = true),
    @SerialName("lbs") Lbs(key = "lbs", isMetric = false),
}

@Serializable
data class RulerOption(
    @SerialName("unit") val unit: MeasurementUnit,
    @SerialName("label") val label: String,
    @SerialName("min") val min: Int,
    @SerialName("max") val max: Int,
    @SerialName("default") val default: Int,
    @SerialName("minor") val minor: Int = 1,
    @SerialName("major") val major: Int = 10,
)

@Serializable
@SerialName("feature_list")
data class FeatureListBlock(
    @SerialName("title") @Serializable(with = StyledTextModelSerializer::class) val title: StyledTextModel? = null,
    @SerialName("items") val items: List<FeatureListItem>,
    @SerialName("emojiHeight") val emojiHeight: Int = 40,
    @SerialName("showCheckmark") val showCheckmark: Boolean = true,
    @SerialName("showDivider") val showDivider: Boolean = true,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
data class FeatureListItem(
    @SerialName("emoji") val emoji: GenderText,
    @SerialName("text") val text: String,
)

@Serializable
@SerialName("progress_slider")
data class ProgressSliderBlock(
    @SerialName("sliders") val sliders: List<ProgressSlider>,
    @SerialName("autoAdvance") val autoAdvance: Boolean = false,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
data class ProgressSlider(
    @SerialName("title") val title: String? = null,
    @SerialName("intervals") val intervals: List<SliderInterval>,
)

@Serializable
data class SliderInterval(
    @SerialName("percentage") val percentage: Int,
    @SerialName("timeInterval") val timeInterval: Int,
    @SerialName("popupMessage") val popupMessage: String? = null,
    @SerialName("answers") val answers: List<String>? = null,
)

@Serializable
@SerialName("review_carousel")
data class ReviewCarouselBlock(
    @SerialName("reviews") val reviews: List<one.healthium.domain.common.ReviewItem>,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("weight_graph")
data class WeightGraphBlock(
    @SerialName("period") val period: String = "3month",
    @SerialName("subtitle") val subtitle: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("bmi_card")
data class BmiCardBlock(
    @SerialName("weightKey") val weightKey: String = "weight",
    @SerialName("heightKey") val heightKey: String = "height",
    @SerialName("categories") val categories: Map<String, BmiCategory>,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
data class BmiCategory(
    @SerialName("emoji") val emoji: String,
    @SerialName("title") val title: String,
    @SerialName("text") val text: String,
)

@Serializable
@SerialName("stack")
data class StackBlock(
    @SerialName("blocks") val blocks: List<UScreenBlock>,
    @SerialName("contentAlignment") val contentAlignment: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("button")
data class ButtonBlock(
    @SerialName("text") val text: String,
    @SerialName("style") val style: ButtonStyle = ButtonStyle.Text,
    @SerialName("action") val action: CtaAction = CtaAction.Next,
    @SerialName("color") val color: String? = null,
    @SerialName("textColor") val textColor: String? = null,
    @SerialName("fullWidth") val fullWidth: Boolean = true,
    @SerialName("heightPx") val heightPx: Int? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
enum class ButtonStyle {
    @SerialName("primary") Primary,
    @SerialName("text") Text,
    @SerialName("outline") Outline,
}

@Serializable
@SerialName("divider")
data class DividerBlock(
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock

@Serializable
@SerialName("notification_preview")
data class NotificationPreviewBlock(
    @SerialName("appName") val appName: String? = null,
    @SerialName("appIcon") val appIcon: PlatformText? = null,
    @SerialName("title") val title: String? = null,
    @SerialName("body") val body: String? = null,
    @SerialName("transition") val transition: BlockTransition? = null,
    @SerialName("padding") val padding: UScreenPadding? = null,
    @SerialName("condition") override val condition: BlockCondition? = null,
) : UScreenBlock {
    companion object {
        val preview = NotificationPreviewBlock(
            appName = "MyApp",
            title = "Don't forget!",
            body = "Time to track your meals",
        )
    }
}
